'use client'

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Image from 'next/image'
import { useTranslations } from 'next-intl'
import {
  cn,
  decodeBase64,
  isDefined,
  Price,
  storeConfigSelector,
  // PriceRange,
  // useBraintreeDropin
  useDrawerScrollBehavior,
  useLazyGetStoreDistanceQuery,
} from '@segway/core'
import { useAppSelector } from '@segway/core/src/store/hooks'
import { useInViewport, useToggle } from 'ahooks'
import isEmpty from 'lodash/isEmpty'

import {
  ACTION_TYPE,
  type ActionType,
  Arrow,
  ChooseStorePopup,
  Dialog,
  ExpandableHTML,
  LocationIcon,
  ShippingIcon,
  Stepper,
  TelephoneIcon,
} from '@/components'
import { Link } from '@/i18n/navigation'
import { Button, Drawer, DrawerContent, DrawerHeader, DrawerTitle } from '@/ui'

import { ProductItem, useProduct, useProductData } from '../../ProductProvider'
import { CartAwaitPopup } from '../cartAwaitPopup'
import { PaypalWidget } from '../paypalWidget'

type ProductOption = {
  required: boolean
  option_id: number
  options: {
    id: number
    quantity: number
    price: number
    is_default: boolean
    product: {
      sku: string
      name: string
      url_key: string
      sw_simple_description: string | null
      short_description: {
        html: string
      }
      saleable_message: {
        is_saleable: boolean
      }
      small_image: {
        url: string
      }
      media_gallery: {
        url: string
      }[]
    }
  }[]
}

const ProductCheckout: React.FC = () => {
  const {
    responsive,
    selectedBundle,
    isEbike,
    isInStock,
    isBundle,
    isNoBundleAndOptions,
    isSubscribe,
    quantity,
    totalPrice,
    totalPriceOrigin,
    savePrice,
    cartStore,
    setQuantity,
    addToCart,
    getStoreAddress,
    productInPartsCategory,
    addProductAlert,
  } = useProduct()
  const product = useProductData()

  const [cartStoreDistance, setCartStoreDistance] = useState('')

  const [getStoreDistance] = useLazyGetStoreDistanceQuery()

  const getBundleOptionsQuery = (
    options: {
      id: number
      value: number
    }[],
  ): string => {
    return options.map((opt) => `bundle_option[${opt.id}]=${opt.value}`).join('&')
  }

  const getFallbackBundleOptions = () => {
    const requiredOption: ProductOption[] = (
      product as ProductItem & { items: ProductOption[] }
    )?.items?.filter((item) => item.required)

    return !isEmpty(requiredOption)
      ? `bundle_option[${requiredOption[0]?.option_id}]=${requiredOption[0]?.options?.[0]?.id}`
      : ''
  }

  const getBundleOptionQueryParameters = () => {
    if (isBundle) {
      if (isEmpty(selectedBundle)) {
        return getFallbackBundleOptions()
      }

      return `${getFallbackBundleOptions()}&${getBundleOptionsQuery(selectedBundle)}`
    }

    return ''
  }

  const bundleOptionQueryParameters = getBundleOptionQueryParameters()

  const productAdditionalData = `product=${product?.id}&selected_configurable_option=&related_product=&item=${product?.id}&${bundleOptionQueryParameters}&qty=${quantity}`

  const parsedPaymentConfig =
    isDefined(product?.payment_config) && product.payment_config.trim() !== ''
      ? JSON.parse(product.payment_config)
      : undefined

  const shippingNoteFormilyData = useMemo(() => {
    return product?.formily_content?.find((item) => item?.code === 'shipping_notes')
  }, [product?.formily_content])

  const shippingNoteData = shippingNoteFormilyData?.data
    ? JSON.parse(shippingNoteFormilyData?.data)?.block_data
    : null

  const [actionType, setActionType] = useState<ActionType>(ACTION_TYPE.changeStore)
  const [isHomeDelivery, setIsHomeDelivery] = useState(false)
  const [showChooseStoreDialog, { toggle: toggleChooseStoreDialog }] = useToggle(false)
  const [showCartAwaitDialog, { toggle: toggleCartAwaitDialog }] = useToggle(false)

  const buttonRef = useRef(null)
  const [inViewport] = useInViewport(buttonRef)

  const t = useTranslations('Product')
  const storeConfig = useAppSelector(storeConfigSelector)

  useDrawerScrollBehavior(showChooseStoreDialog)

  /**
   * 点击加购
   */
  const handleAddToCart = async (type = false) => {
    if (isEbike) {
      setActionType(ACTION_TYPE.pickup)
      setIsHomeDelivery(type)
      toggleChooseStoreDialog()
      return
    }

    const result = await addToCart(type)
    if (result && toggleCartAwaitDialog) {
      toggleCartAwaitDialog()
    }
  }

  /**
   * 点击test ride
   */
  const handleTestRide = () => {
    setActionType(ACTION_TYPE.testRide)
    toggleChooseStoreDialog()
  }

  /**
   * 点击 change store
   */
  const handleChangeStore = () => {
    setActionType(ACTION_TYPE.changeStore)
    toggleChooseStoreDialog()
  }

  /**
   * 获取门店列表
   */
  const fetchStoreList = useCallback(
    (latAndLng: { lat?: number; lng?: number; id?: number } = {}) => {
      getStoreDistance(
        latAndLng.lat && latAndLng.lng
          ? {
              latitude: latAndLng.lat.toString(),
              longitude: latAndLng.lng.toString(),
              pickupStore: latAndLng.id,
            }
          : {},
      )
        .unwrap()
        .then((res) => {
          if (res?.getStoreList?.length) {
            setCartStoreDistance(res?.getStoreList[0]?.distance_text || '')
          }
        })
        .catch((err) => {
          console.log(err)
        })
    },
    [getStoreDistance],
  )

  /**
   * 获取用户当前位置
   */
  useEffect(() => {
    if (isEbike && cartStore?.store_id) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          fetchStoreList({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            id: cartStore.store_id!,
          })
        },
        () => {},
        {
          // 可选配置项
          enableHighAccuracy: true,
          timeout: 5000,
          maximumAge: 0,
        },
      )
    }
  }, [fetchStoreList, isEbike, cartStore])

  return (
    <>
      {isEbike && cartStore?.id && (
        <div>
          <div className="mb-2 mt-9 flex items-center justify-between">
            <h4 className="font-miSansBold630 text-[20px] text-gray-8">Nearby Store</h4>
            <div onClick={handleChangeStore} className="flex cursor-pointer items-center gap-1">
              <span className="font-miSansMedium380 text-[16px] text-gray-8">Change Store</span>
              <Arrow width={16} height={16} color="#6E6E73" />
            </div>
          </div>
          <div className="my-4 rounded-md border border-gray-2 px-4 py-6">
            <div className="flex items-center justify-between">
              <p className="line-clamp-1 font-miSansMedium380 text-lg text-gray-8 xl:font-miSansDemiBold450 xl:text-xl">
                {cartStore?.store_name}
              </p>
              {cartStoreDistance && (
                <div className="flex items-center gap-1">
                  <LocationIcon />
                  <p className="font-miSansRegular330 text-[14px] text-gray-4">
                    {cartStoreDistance}
                  </p>
                </div>
              )}
            </div>
            <div className="my-3 flex items-center justify-between gap-1 xl:mb-0 xl:mt-2">
              <p className="max-w-[332px] font-miSansRegular330 text-base text-gray-5 xl:text-gray-6">
                {getStoreAddress(cartStore)}
              </p>
              <div className="hidden xl:block">
                <div className="flex items-center gap-1">
                  <TelephoneIcon />
                  <span className="ml-[7px] font-miSansRegular330 text-[14px] text-gray-6">
                    {cartStore.telephone}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1 xl:hidden">
              <TelephoneIcon />
              <Link
                className="ml-[7px] font-miSansRegular330 text-[14px] text-[#007AFF]"
                href={`tel:${cartStore.telephone}`}>
                {cartStore.telephone}
              </Link>
            </div>
          </div>
        </div>
      )}

      {isInStock && (
        <div
          className={cn('mt-[20px] rounded-md border-[#D1D1D4] xl:border xl:px-[19px] xl:py-9', {
            'border-none xl:px-0 xl:py-0': isNoBundleAndOptions,
          })}>
          {!isNoBundleAndOptions && (
            <div className="mb-[18px] flex items-center gap-3 xl:mb-[20px]">
              <div className="flex h-[88px] w-[88px] overflow-hidden rounded-md">
                <Image
                  src={product?.small_image?.url || ''}
                  alt="product"
                  width={88}
                  height={88}
                  style={{ objectFit: 'contain' }}
                  className="bg-gray-1"
                />
              </div>

              <div className="flex-1">
                <p className="line-clamp-2 font-miSansDemiBold450 text-base leading-[20px] text-gray-8 xl:text-lg">
                  {product?.name}
                </p>
                {/* <div className="mt-2">
                {product?.price_range && <PriceRange priceRange={product.price_range} />}
              </div> */}
                <div className="mt-3 flex items-center">
                  <span className="font-miSansRegular330 text-base text-black">×{quantity}</span>
                </div>
              </div>
            </div>
          )}

          {!!selectedBundle?.length && <div className="mb-7 h-[1px] bg-gray-1"></div>}

          {!!selectedBundle?.length &&
            selectedBundle.map((item) => (
              <div key={item.sku} className="mb-7 ml-4 flex items-center gap-3">
                <div className="flex h-[72px] w-[72px] overflow-hidden rounded-md">
                  <Image
                    src={item.small_image}
                    alt="product"
                    width={72}
                    height={72}
                    style={{ objectFit: 'contain' }}
                    className="bg-gray-1"
                  />
                </div>
                <div className="flex-1">
                  <p className="line-clamp-2 font-miSansDemiBold450 text-[16px] text-gray-8">
                    {item.name}
                  </p>
                  {/* <div className="mt-2">
                    <Price color="primary" price={{ value: item.price || 0 }} />
                  </div> */}
                  <div className="mt-3 flex items-center">
                    <span className="font-miSansRegular330 text-base text-black">×{quantity}</span>
                  </div>
                </div>
              </div>
            ))}

          <div
            className={cn('flex items-center justify-between py-0 xl:py-2', {
              'xl:py-0': isNoBundleAndOptions,
            })}>
            {isNoBundleAndOptions && (
              <h3 className="font-miSansSemiBold520 text-2xl leading-none text-gray-8">
                {t('quantity')}
              </h3>
            )}
            <Stepper
              value={quantity}
              inputLeftClass="border-gray-1"
              inputRightClass="border-gray-1"
              inputClass="border-gray-1"
              onChange={setQuantity}
              iconLeftWidth={responsive && responsive.xl ? 16 : 8}
              iconLeftHeight={2}
              iconRightWidth={responsive && responsive.xl ? 16 : 8}
              iconRightHeight={responsive && responsive.xl ? 16 : 8}
            />
          </div>

          {/* Price */}
          {!isNoBundleAndOptions && (
            <div className="mt-[18px] flex items-center xl:mt-7 xl:pb-1.5">
              <Price
                color={savePrice > 0 ? 'primary' : 'default'}
                price={{ value: totalPrice }}
                currencyStyle="xl:text-[20px] text-base"
                textStyle="xl:text-[28px] text-xl leading-[120%]"
                fractionStyle="xl:text-[28px] text-xl"
              />
              {savePrice > 0 && (
                <div className="ml-2">
                  <Price
                    color={'gray'}
                    variant="lineThrough"
                    price={{ value: totalPriceOrigin }}
                    currencyStyle="xl:text-[20px] text-base"
                    textStyle="xl:text-[28px] text-xl"
                    fractionStyle="xl:text-[28px] text-xl"
                  />
                </div>
              )}
            </div>
          )}
          {!isNoBundleAndOptions && storeConfig?.is_price_include_tax && (
            <div className="flex items-center justify-between">
              <span>({t('vat_included')})</span>
            </div>
          )}

          <div className="my-[17px] h-[1px] bg-gray-1 xl:my-7"></div>

          {/* Free Shipping Note */}
          <div>
            {!isEbike && shippingNoteData && (
              <div className="flex items-center gap-2">
                {responsive && responsive.xl && <ShippingIcon width={24} height={24} />}
                <h3 className="font-miSansDemiBold450 text-base leading-[19px] text-gray-8 xl:text-[20px] xl:leading-[1]">
                  {shippingNoteData?.title}
                </h3>
              </div>
            )}
            {shippingNoteData?.content && (
              <ExpandableHTML htmlContent={shippingNoteData?.content} />
            )}
          </div>

          {/* Buttons */}
          <div
            id="pdp-checkout"
            className={cn('mt-7 flex flex-col space-y-3 xl:mt-[44px] xl:space-y-4', {
              'mt-9 xl:mt-9': isNoBundleAndOptions,
            })}>
            {isEbike ? (
              <>
                <Button
                  ref={buttonRef}
                  onClick={() => handleAddToCart()}
                  className="h-[44px] w-full text-lg xl:h-[64px] xl:text-2xl">
                  Pickup in store
                </Button>
                <Button
                  variant="secondary"
                  outline="secondary"
                  onClick={() => handleAddToCart(true)}
                  className="h-[44px] w-full xl:h-[64px] xl:text-2xl">
                  Home Delivery
                </Button>
                <Button
                  variant="secondary"
                  outline="secondary"
                  onClick={handleTestRide}
                  className="h-[44px] w-full xl:h-[64px] xl:text-2xl">
                  Reserve a Test Ride
                </Button>
              </>
            ) : (
              <>
                <div
                  className={`${responsive && responsive.md ? 'flex w-full items-center justify-center' : ''}`}>
                  <Button
                    ref={buttonRef}
                    onClick={() => handleAddToCart()}
                    className={`h-[44px] text-lg xl:text-2xl ${responsive && responsive.md ? 'w-[750px]' : 'w-full'}`}>
                    {t('add_to_cart')}
                  </Button>
                </div>
                {!isEbike && parsedPaymentConfig?.paypal?.show && (
                  <PaypalWidget
                    pageType="pdp"
                    {...{
                      totalPrice,
                      productAdditionalData,
                      parsedPaymentConfig,
                    }}
                  />
                )}
              </>
            )}
          </div>

          {/* Warning */}
          <div id="product-warning" />

          {productInPartsCategory && (
            <div className="mt-4 rounded-md bg-gray-0 px-2 py-3 text-center xl:mt-7 xl:p-4">
              <Link
                href="https://service.segway.com/selfRepair?goType=3"
                target="_blank"
                className="font-miSansDemiBold450 text-sm text-gray-8 underline xl:text-lg">
                Click to learn about official maintenance instructions
              </Link>
            </div>
          )}
        </div>
      )}

      {!isInStock && isEbike && (
        <div ref={buttonRef} className="mt-7 flex flex-col space-y-5 xl:mt-[44px] xl:space-y-4">
          <Button
            outline="secondary"
            onClick={handleTestRide}
            className="h-[44px] w-full border-black xl:h-[64px] xl:text-2xl">
            Reserve a Test Ride
          </Button>
        </div>
      )}

      {responsive && responsive.xl && (
        <div
          className={cn(
            'fixed bottom-0 left-0 z-20 w-full bg-white py-[14px] shadow-[0px_-8px_12px_0px_rgba(0,0,0,0.04)] transition',
            inViewport ? 'translate-y-[80px] opacity-0' : 'translate-y-0 opacity-100',
          )}>
          <div className="max-page-content mx-auto flex items-center justify-between">
            <div>
              <p className="font-miSansDemiBold450 text-xl leading-[24px] text-gray-6">
                {product?.name}
              </p>
              {!!selectedBundle?.length && (
                <div className="mt-1.5 flex flex-wrap items-center gap-4">
                  {selectedBundle.map((item) => (
                    <span
                      key={item.sku}
                      className="font-miSansMedium380 text-sm leading-[16px] text-gray-5">
                      {item.name} x {quantity}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-center gap-7">
              {storeConfig?.is_price_include_tax && (
                <div className="flex w-full items-center">
                  <span>({t('vat_included')})</span>
                </div>
              )}
              <Price
                color={savePrice > 0 ? 'primary' : 'default'}
                price={{ value: totalPrice }}
                currencyStyle="xl:text-[20px] text-xl font-miSansDemiBold450"
                textStyle="xl:text-[28px] text-xl font-miSansSemiBold520"
                fractionStyle="xl:text-[20px] text-xl font-miSansSemiBold520"
              />
              {isInStock && !isEbike && (
                <Button onClick={() => handleAddToCart()} className="h-[50px] w-full px-9 text-lg">
                  {t('add_to_cart')}
                </Button>
              )}
              {isInStock && isEbike && (
                <Button onClick={() => handleAddToCart()} className="h-[50px] w-full px-9 text-lg">
                  Pick up In-Store
                </Button>
              )}
              {!isInStock && isEbike && (
                <Button onClick={handleTestRide} className="h-[50px] w-full px-9 text-lg">
                  Reserve a Test Ride
                </Button>
              )}
              {!isInStock && !isEbike && (
                <Button
                  variant={isSubscribe ? 'secondary' : 'default'}
                  disabled={isSubscribe}
                  onClick={() => {
                    if (isSubscribe) {
                      return
                    }
                    addProductAlert(Number(decodeBase64(product?.uid || '')))
                  }}
                  className={cn('h-[50px] w-full px-9 text-lg')}>
                  {isSubscribe ? t('notified') : t('notify_when_available')}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {responsive && !responsive.xl && (
        <div className={`fixed bottom-0 left-0 z-20 w-full overflow-y-hidden`}>
          <div
            className={cn(
              'w-full bg-white px-4 pt-[10px] shadow-[0px_-8px_12px_0px_rgba(0,0,0,0.04)] transition',
              inViewport ? 'translate-y-[60px] opacity-0' : 'translate-y-0 opacity-100',
            )}
            style={{
              paddingBottom: 'calc(env(safe-area-inset-bottom) + 10px)',
            }}>
            <div className="flex items-center justify-between">
              <Price
                bold
                price={{ value: totalPrice }}
                color={savePrice > 0 ? 'primary' : 'default'}
                currencyStyle="text-base font-miSansDemiBold450"
                textStyle="text-[22px] font-miSansDemiBold450"
                fractionStyle="text-base font-miSansDemiBold450"
              />
              {isInStock && !isEbike && (
                <Button onClick={() => handleAddToCart()} className="h-[44px] px-8 text-base">
                  Add To Cart
                </Button>
              )}
              {!isInStock && !isEbike && (
                <Button
                  onClick={() => {
                    if (isSubscribe) {
                      return
                    }
                    addProductAlert(Number(decodeBase64(product?.uid || '')))
                  }}
                  className={cn(
                    'h-[44px] px-8 text-base',
                    isSubscribe
                      ? 'cursor-default bg-gray-2 text-[rgba(0,0,0,0.3)] hover:bg-gray-2'
                      : '',
                  )}>
                  {isSubscribe ? t('notified') : t('notify_when_available')}
                </Button>
              )}
              {isInStock && isEbike && (
                <Button onClick={() => handleAddToCart()} className="h-[44px] px-8 text-base">
                  Pick up In-Store
                </Button>
              )}
              {!isInStock && isEbike && (
                <Button onClick={handleTestRide} className="h-[44px] px-8 text-base">
                  Reserve a Test Ride
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Choose Store Dialog */}
      {responsive && responsive.xl && (
        <Dialog
          open={showChooseStoreDialog}
          onOpenChange={toggleChooseStoreDialog}
          containerStyle="w-[712px]"
          titleStyle="font-normal xl:h-[34px]"
          title={actionType === ACTION_TYPE.testRide ? 'Book a Test Ride' : 'Select store'}
          // description="This item can only be picked up in-store"
        >
          <ChooseStorePopup
            actionType={actionType}
            isHomeDelivery={isHomeDelivery}
            toggleChooseStoreDialog={toggleChooseStoreDialog}
            toggleCartAwaitDialog={toggleCartAwaitDialog}
          />
        </Dialog>
      )}
      {/* https://github.com/shadcn-ui/ui/issues/2831#issuecomment-2646654978 */}
      {/* https://github.com/radix-ui/primitives/issues/3119#issuecomment-2683308810 */}
      {/* https://github.com/emilkowalski/vaul/issues/294#issuecomment-2424041895 */}
      {responsive && !responsive.xl && (
        <Drawer
          repositionInputs={false}
          fixed
          autoFocus={false}
          open={showChooseStoreDialog}
          onOpenChange={toggleChooseStoreDialog}>
          <DrawerContent className="max-h-[550px] xl:max-h-[620px]">
            <DrawerHeader className="border-none">
              <DrawerTitle className="py-7 font-miSansDemiBold450 text-3xl text-gray-8">
                {actionType === ACTION_TYPE.testRide ? 'Book a Test Ride' : 'Select store'}
              </DrawerTitle>
              {/* <p className="pb-5 font-miSansMedium380 text-base text-gray-8">
                This item can only be picked up in-store
              </p> */}
            </DrawerHeader>
            <ChooseStorePopup
              actionType={actionType}
              isHomeDelivery={isHomeDelivery}
              toggleChooseStoreDialog={toggleChooseStoreDialog}
              toggleCartAwaitDialog={toggleCartAwaitDialog}
            />
          </DrawerContent>
        </Drawer>
      )}

      {/* Cart Await Dialog */}
      {responsive && responsive.xl && (
        <Dialog
          open={showCartAwaitDialog}
          onOpenChange={toggleCartAwaitDialog}
          containerStyle="w-[712px]"
          title={t('you_cart_await')}
          titleStyle="pb-3">
          <CartAwaitPopup
            isHomeDelivery={isHomeDelivery}
            toggleCartAwaitDialog={toggleCartAwaitDialog}
          />
        </Dialog>
      )}
      {responsive && !responsive.xl && (
        <Drawer open={showCartAwaitDialog} onOpenChange={toggleCartAwaitDialog}>
          <DrawerContent>
            <DrawerHeader className="border-none pb-4 pt-7">
              <DrawerTitle className="py-0 font-miSansDemiBold450 text-4xl leading-[1.4] text-gray-8">
                {t('you_cart_await')}
              </DrawerTitle>
            </DrawerHeader>
            <CartAwaitPopup
              isHomeDelivery={isHomeDelivery}
              toggleCartAwaitDialog={toggleCartAwaitDialog}
            />
          </DrawerContent>
        </Drawer>
      )}
    </>
  )
}

export default ProductCheckout
